// Problem Reporter — encrypted offline report queue.
//
// A SEPARATE IndexedDB (avsuite-reports-v1), deliberately independent of the tuned offline-sync
// DB (avsuite-offline-v1) — we never add a store to it or bump OFFLINE_DB_VERSION. Each pending
// report is encrypted AT REST under the device DEK (the same crypto.encryptJson envelope the
// op_queue uses), so no plaintext PII (incl. the screenshot) ever sits on disk.
//
// DEK RULE (design §6): enqueue REQUIRES an unlocked DEK. If there is no DEK (an un-enrolled
// device used offline), enqueue refuses — the Report button has already degraded to
// "reconnect to send" for that case, so we never fall back to plaintext storage.
//
// Flush is triggered by the app's existing reconnect signal (subscribeConnection) — see
// ReportProblemButton. The client_report_id makes a re-tried flush idempotent server-side.

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { getDek } from './dekSession';
import { encryptJson, decryptJson, type Envelope } from './crypto';
import { apiFetch } from '../api';

export const REPORT_DB_NAME = 'avsuite-reports-v1';
const REPORT_DB_VERSION = 1;
const STORE = 'reports';

// The exact JSON body POST /api/reports accepts (server stamps tenant_id/user_id — NOT here).
export interface ReportPayload {
  client_report_id: string;
  client_created_at: string;
  submitted_offline?: boolean;
  route: string;
  app_version: string;
  user_agent: string;
  online: boolean;
  queue_depth: number;
  console_tail?: unknown[];
  network_fails?: unknown[];
  description: string;
  expected?: string;
  screenshot?: string;       // base64 (no data-URL prefix)
  screenshot_mime?: 'image/jpeg' | 'image/png';
}

interface ReportRow {
  client_report_id: string;  // keyPath — also the server idempotency key
  env: Envelope;             // encryptJson(dek, payload)
}

interface ReportDB extends DBSchema {
  reports: { key: string; value: ReportRow };
}

let dbPromise: Promise<IDBPDatabase<ReportDB>> | null = null;
function getReportDb(): Promise<IDBPDatabase<ReportDB>> {
  if (!dbPromise) {
    dbPromise = openDB<ReportDB>(REPORT_DB_NAME, REPORT_DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'client_report_id' });
        }
      },
    });
  }
  return dbPromise;
}

/**
 * Encrypt + store a pending report. Requires an unlocked DEK — returns false (does NOT store
 * plaintext) when locked/un-enrolled. The caller must have degraded the UI accordingly.
 */
export async function enqueueReport(payload: ReportPayload): Promise<boolean> {
  const dek = getDek();
  if (!dek) return false; // no DEK → never store plaintext PII at rest
  try {
    const db = await getReportDb();
    const env = await encryptJson(dek, { ...payload, submitted_offline: true });
    await db.put(STORE, { client_report_id: payload.client_report_id, env });
    return true;
  } catch (e) {
    console.warn('[reports] enqueueReport failed', e);
    return false;
  }
}

/** Number of pending (queued) reports — UI/diagnostics helper. */
export async function countQueuedReports(): Promise<number> {
  try {
    const db = await getReportDb();
    return (await db.getAllKeys(STORE)).length;
  } catch {
    return 0;
  }
}

let flushing = false;

/**
 * Decrypt + POST each queued report, removing it on a successful (or idempotent-duplicate)
 * 2xx response. Tolerant of partial failure: a network throw stops the run and leaves the
 * rest queued; an unreadable row (wrong DEK / tamper) is skipped. Single-flight via `flushing`.
 */
export async function flushReports(userId: string): Promise<{ flushed: number; remaining: number }> {
  const dek = getDek();
  if (!dek || flushing) return { flushed: 0, remaining: await countQueuedReports() };
  flushing = true;
  let flushed = 0;
  try {
    const db = await getReportDb();
    const rows = await db.getAll(STORE);
    for (const row of rows) {
      let payload: ReportPayload;
      try {
        payload = await decryptJson<ReportPayload>(dek, row.env);
      } catch {
        continue; // unreadable (wrong DEK / tampered) — skip, leave in place
      }
      try {
        const res = await apiFetch<{ id: string }>('/api/reports', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        const ok = (res.status ?? 0) >= 200 && (res.status ?? 0) < 300 && !!res.data?.id;
        if (ok) {
          await db.delete(STORE, row.client_report_id);
          flushed += 1;
        }
        // Non-2xx (e.g. transient 5xx): leave queued for the next reconnect.
      } catch {
        // NetworkError — connection dropped again; stop and keep the remainder queued.
        break;
      }
    }
    const remaining = (await db.getAllKeys(STORE)).length;
    if (flushed > 0) console.info(`[reports] flushed ${flushed} queued report(s) for ${userId}; ${remaining} remaining`);
    return { flushed, remaining };
  } catch (e) {
    console.warn('[reports] flushReports failed', e);
    return { flushed, remaining: await countQueuedReports() };
  } finally {
    flushing = false;
  }
}
